import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/use-local-storage';

export type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'theme-preference';

type ThemeContextValue = {
    /** The user's stored preference. */
    theme: Theme;
    /** The effective theme after resolving `system`. */
    resolvedTheme: ResolvedTheme;
    setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const prefersDark = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveTheme = (theme: Theme): ResolvedTheme =>
    theme === 'system' ? (prefersDark() ? 'dark' : 'light') : theme;

const applyTheme = (resolved: ResolvedTheme) => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document.documentElement.style.colorScheme = resolved;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setStoredTheme] = useLocalStorage<Theme>(THEME_STORAGE_KEY, 'system');

    const resolvedTheme = resolveTheme(theme);

    // Apply the resolved theme to the document whenever the preference changes.
    useEffect(() => {
        applyTheme(resolveTheme(theme));
    }, [theme]);

    // While in `system` mode, follow live OS appearance changes.
    useEffect(() => {
        if (theme !== 'system') return;

        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => applyTheme(media.matches ? 'dark' : 'light');

        media.addEventListener('change', handleChange);
        return () => media.removeEventListener('change', handleChange);
    }, [theme]);

    const setTheme = useCallback(
        (next: Theme) => {
            setStoredTheme(next);
            applyTheme(resolveTheme(next));
        },
        [setStoredTheme],
    );

    const value = useMemo<ThemeContextValue>(
        () => ({ theme, resolvedTheme, setTheme }),
        [theme, resolvedTheme, setTheme],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
